/**
 * Chapter008.js — Objectives & Targets (OHSE 008)
 * Migrated from SWMS Platform Chapter008.tsx → JavaScript
 *
 * Props:
 *   data        {array}   objectives_targets from swmsData
 *   onChange    {fn}      (newEntries) => void
 *   companyName {string}  from companyInfo.name
 */

import { useCallback } from 'react';
import { Plus, Trash2 } from 'lucide-react';

const inputCls = 'w-full px-2 py-1.5 text-sm bg-transparent border border-[#e0dbd0] rounded text-[#1a1a2e] placeholder:text-gray-400 focus:outline-none focus:border-[#c9a84c] focus:bg-white transition-all';
const textareaCls = 'w-full px-2 py-1.5 text-sm bg-transparent border border-[#e0dbd0] rounded text-[#1a1a2e] placeholder:text-gray-400 focus:outline-none focus:border-[#c9a84c] focus:bg-white transition-all resize-y min-h-[72px]';
const selectCls = 'w-full h-9 px-2 text-sm border border-[#e0dbd0] rounded bg-white text-[#1a1a2e] focus:outline-none focus:border-[#c9a84c]';

const CATEGORIES = ['Planning', 'Risk Management', 'Consultation', 'Training'];

function categoryBadgeCls(category) {
  switch (category) {
    case 'Planning':      return 'bg-blue-50 text-blue-800 border-blue-200';
    case 'Risk Management': return 'bg-red-50 text-red-800 border-red-200';
    case 'Consultation':  return 'bg-amber-50 text-amber-800 border-amber-200';
    case 'Training':      return 'bg-green-50 text-green-800 border-green-200';
    default:              return 'bg-gray-50 text-gray-500 border-gray-200';
  }
}

function emptyEntry() {
  return { id: crypto.randomUUID(), category: '', objective: '', target: '' };
}

export default function Chapter008({ data = [], onChange, companyName }) {
  const cn = companyName || '[Company Name]';

  const addItem = useCallback(() => onChange([...data, emptyEntry()]), [data, onChange]);
  const removeItem = useCallback((id) => onChange(data.filter(e => e.id !== id)), [data, onChange]);
  const updateItem = useCallback((id, field, value) => {
    onChange(data.map(e => e.id === id ? { ...e, [field]: value } : e));
  }, [data, onChange]);

  return (
    <div className="space-y-6">
      {/* Header */}
      <div>
        <p className="text-xs font-semibold text-[#c9a84c] uppercase tracking-widest mb-1">OHSE 008</p>
        <h2 className="text-xl font-bold text-[#1a1a2e] uppercase tracking-wide">Objectives &amp; Targets</h2>
      </div>

      {/* Declaration */}
      <div className="p-4 bg-[#f8f7f4] rounded-lg border border-[#e0dbd0] text-sm text-[#1a1a2e] leading-relaxed">
        <span className="font-semibold text-[#c9a84c]">{cn}</span> has established the following
        objectives and targets to support and maintain the effectiveness of the OHSE Management Plan.
      </div>

      {/* Objective cards */}
      {data.map((item, idx) => (
        <div key={item.id} className="bg-white border border-[#e0dbd0] rounded-lg overflow-hidden">
          {/* Card header */}
          <div className="px-4 py-2.5 bg-[#f0ede6] flex items-center justify-between">
            <div className="flex items-center gap-3">
              <span className="flex items-center justify-center w-6 h-6 rounded-full bg-[#1a1a2e] text-white text-xs font-semibold shrink-0">
                {idx + 1}
              </span>
              {item.category ? (
                <span className={`inline-flex px-2.5 py-0.5 rounded-full text-xs font-semibold border ${categoryBadgeCls(item.category)}`}>
                  {item.category}
                </span>
              ) : (
                <span className="inline-flex px-2.5 py-0.5 rounded-full text-xs font-semibold border bg-gray-50 text-gray-400 border-gray-200">
                  Uncategorized
                </span>
              )}
            </div>
            <button type="button" onClick={() => removeItem(item.id)}
              className="p-1 text-red-400 hover:text-red-600 transition-colors">
              <Trash2 className="w-4 h-4" />
            </button>
          </div>

          <div className="p-4 space-y-3">
            {/* Category */}
            <div>
              <label className="block text-xs font-medium text-gray-500 mb-1">Category</label>
              <select value={item.category} onChange={e => updateItem(item.id, 'category', e.target.value)} className={selectCls}>
                <option value="">Select category</option>
                {CATEGORIES.map(c => <option key={c} value={c}>{c}</option>)}
              </select>
            </div>

            {/* Objective */}
            <div className="p-3 bg-[#f8f7f4] rounded border-l-4 border-[#c9a84c]">
              <label className="block text-xs font-semibold text-[#c9a84c] uppercase tracking-wider mb-1.5">Objective</label>
              <textarea value={item.objective} placeholder="Describe the OHSE objective..."
                onChange={e => updateItem(item.id, 'objective', e.target.value)} className={textareaCls} />
            </div>

            {/* Target */}
            <div className="p-3 bg-white rounded border-l-4 border-[#1a1a2e]">
              <label className="block text-xs font-semibold text-[#1a1a2e] uppercase tracking-wider mb-1.5">Target</label>
              <textarea value={item.target} placeholder="Describe the target to achieve this objective..."
                onChange={e => updateItem(item.id, 'target', e.target.value)} className={textareaCls} />
            </div>
          </div>
        </div>
      ))}

      {data.length === 0 && (
        <div className="flex flex-col items-center justify-center py-12 text-center border border-[#e0dbd0] rounded-lg bg-white">
          <p className="text-sm text-gray-400 mb-3">No objectives and targets defined yet.</p>
          <button type="button" onClick={addItem}
            className="inline-flex items-center gap-1.5 px-4 py-2 text-sm text-white bg-[#1a1a2e] rounded hover:bg-[#252547] transition-colors">
            <Plus className="w-4 h-4" /> Add Objective &amp; Target
          </button>
        </div>
      )}

      {data.length > 0 && (
        <div className="flex justify-center">
          <button type="button" onClick={addItem}
            className="inline-flex items-center gap-1.5 px-3 py-1.5 text-sm text-[#1a1a2e] rounded hover:bg-[rgba(201,168,76,0.15)] border border-[#c9a84c]/40 transition-colors">
            <Plus className="w-4 h-4" /> Add Objective &amp; Target
          </button>
        </div>
      )}
    </div>
  );
}
